import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Route, FileText, Handshake, Gift, Phone, Zap, Loader2, Send,
  CheckCircle2, Building2, Mail, User, MessageSquare, Truck, ExternalLink,
} from "lucide-react";
import { API, BRAND } from "../constants/brand";

const BENEFITS = [
  {
    icon: Route,
    title: "Live-Sendungsverfolgung",
    text: "Jede Sendung erhält eine Tour-Nr. — Sie und Ihre Kunden verfolgen den Status jederzeit live, inklusive Empfangsbestätigung mit Unterschrift.",
  },
  {
    icon: FileText,
    title: "Korrekte Rechnungen",
    text: "Rechnung mit ausgewiesener 19 % MwSt. zu jedem Transport — auf Wunsch als monatliche Sammelrechnung für Ihre Buchhaltung.",
  },
  {
    icon: Handshake,
    title: "Rahmenvertrag & Festpreise",
    text: "Feste Routen, fester Kilometerpreis, monatliche Abrechnung: planbare Kosten statt Einzelanfragen — ideal für wiederkehrende Werkverkehre.",
  },
  {
    icon: Gift,
    title: "11. Transport kostenlos",
    text: "Unser Treuebonus für Stammkunden: nach 10 Transporten fährt der 11. kostenlos — automatisch, ohne Papierkram.",
  },
  {
    icon: Phone,
    title: "Persönlicher Ansprechpartner",
    text: "Kurze Wege: Sie erreichen die Disposition direkt per Telefon oder WhatsApp — keine Hotline, keine Warteschleife.",
  },
  {
    icon: Zap,
    title: "Deutschlandweit & Express",
    text: "Vom Express-Paket bis zur Palettenware, deutschlandweit — Abholung oft noch am selben Tag.",
  },
];

const STEPS = [
  { n: "01", t: "Anfrage senden", d: "Formular unten, E-Mail oder WhatsApp — schildern Sie kurz Strecke und Volumen." },
  { n: "02", t: "Festpreis-Angebot", d: "Sie erhalten innerhalb von 24 Stunden ein verbindliches Angebot mit Festpreis pro Kilometer." },
  { n: "03", t: "Rahmenvertrag", d: "Wir fixieren Routen, Preise und Abrechnungsrhythmus — unkompliziert auf einer Seite." },
  { n: "04", t: "Monatliche Rechnung", d: "Alle Fahrten gesammelt auf einer Rechnung mit MwSt. — fertig für Ihre Buchhaltung." },
];

const inputCls =
  "w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 transition-colors";

export default function BusinessPage() {
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: `${form.name} (${form.company}) — Firmenkunde`,
        email: form.email,
        message: `FIRMA: ${form.company}\nTELEFON: ${form.phone}\n\n${form.message}`,
      });
      setDone(true);
      toast.success("Anfrage gesendet — wir melden uns innerhalb von 24 Stunden.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder rufen Sie uns an.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="b2b-page" className="min-h-screen bg-[#0A0E14] text-slate-200">
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt={`${BRAND.name} Logo`} className="w-9 h-9 rounded-lg object-cover" />
            <div>
              <div className="font-semibold text-white text-sm">{BRAND.name}</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-500 font-mono-tech">{BRAND.tagline}</div>
            </div>
          </a>
          <a
            data-testid="b2b-back-link"
            href="/"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5">
        {/* Hero */}
        <section className="py-14 lg:py-20 max-w-3xl">
          <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-cyan-400 mb-4 flex items-center gap-2">
            <Building2 size={13} /> Für Firmenkunden & Industrie · Karlsruhe
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white leading-[1.08]">
            Zuverlässiger Gütertransport <span className="text-gradient-blue">für Ihr Unternehmen.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl">
            Feste Routen, Festpreise und ein Partner, der liefert: M-Line Solutions übernimmt Ihre Werkverkehre und
            Zustellfahrten — deutschlandweit, mit Live-Tracking, Empfangsbestätigung und ordentlicher Rechnung.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid="b2b-cta-angebot"
              href="#b2b-kontakt"
              className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_-12px_rgba(30,111,217,0.7)] transition-transform duration-300 hover:scale-[1.04]"
            >
              Angebot anfordern
            </a>
            <a
              href={`tel:${BRAND.phone}`}
              className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-slate-200 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors"
            >
              <Phone size={15} /> {BRAND.phone}
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-8 font-mono-tech">
            <div>
              <div className="text-xl font-bold text-slate-50">ab 50 €</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Grundpreis inkl. 15 km, danach 0,80–1,50 €/km</div>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-50">bis 4,20 m</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Ladefläche (Transit)</div>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-50">11.</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Transport gratis</div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="pb-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map((b) => (
              <div
                key={b.title}
                className="rounded-2xl border border-white/8 bg-[#121826] p-6 hover:border-cyan-400/30 transition-colors duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1E6FD9]/20 to-[#29B6F6]/10 border border-cyan-400/25 flex items-center justify-center mb-4">
                  <b.icon size={20} className="text-cyan-400" />
                </div>
                <h3 className="text-base font-bold text-slate-50 mb-2">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="pb-16">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-50 mb-8">
            So einfach starten wir <span className="text-gradient-blue">zusammen</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
                <div className="font-mono-tech text-cyan-400 text-sm font-bold mb-2">{s.n}</div>
                <div className="font-semibold text-slate-100 text-sm mb-1.5">{s.t}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact form */}
        <section id="b2b-kontakt" className="pb-20 scroll-mt-8">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-50">
                Fordern Sie Ihr <span className="text-gradient-blue">Festpreis-Angebot</span> an
              </h2>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                Schildern Sie kurz Strecke, Volumen und Turnus — Sie erhalten innerhalb von 24 Stunden ein
                verbindliches Angebot. Oder rufen Sie direkt an:{" "}
                <a href={`tel:${BRAND.phone}`} className="text-cyan-300 hover:underline">{BRAND.phone}</a>
              </p>
              <div className="mt-6 space-y-2.5 text-sm text-slate-400">
                <div className="flex items-center gap-2.5"><CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> Antwort innerhalb von 24 Stunden</div>
                <div className="flex items-center gap-2.5"><CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> Verbindlicher Festpreis, keine versteckten Kosten</div>
                <div className="flex items-center gap-2.5"><CheckCircle2 size={15} className="text-emerald-400 shrink-0" /> Unverbindlich & kostenlos</div>
              </div>
              <a
                data-testid="b2b-partner-badge"
                href="https://www.carboncleangermany.de"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 max-w-sm hover:border-cyan-400/40 transition-colors group"
              >
                <img src="/partner-carbonclean.jpg" alt="CarbonCleanGermany — Motor Carbon Clean Logo" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 font-mono-tech">Unser Partner</div>
                  <div className="text-sm font-semibold text-slate-100 group-hover:text-cyan-300 transition-colors flex items-center gap-1.5">
                    CarbonCleanGermany <ExternalLink size={12} className="text-cyan-400" />
                  </div>
                </div>
              </a>
            </div>

            <div className="lg:col-span-3">
              {done ? (
                <div data-testid="b2b-success" className="rounded-3xl border border-emerald-400/25 bg-emerald-400/5 p-10 text-center h-full flex flex-col items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Vielen Dank für Ihre Anfrage!</h3>
                  <p className="text-sm text-slate-400 max-w-sm">
                    Wir melden uns innerhalb von 24 Stunden mit einem verbindlichen Angebot bei Ihnen.
                  </p>
                </div>
              ) : (
                <form
                  data-testid="b2b-form"
                  onSubmit={submit}
                  className="rounded-3xl border border-white/8 bg-[#121826] p-6 lg:p-8 space-y-4"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="relative">
                      <Building2 size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      <input data-testid="b2b-form-company" value={form.company} onChange={set("company")} placeholder="Firma *" required className={`${inputCls} pl-10`} />
                    </div>
                    <div className="relative">
                      <User size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      <input data-testid="b2b-form-name" value={form.name} onChange={set("name")} placeholder="Ansprechpartner *" required className={`${inputCls} pl-10`} />
                    </div>
                    <div className="relative">
                      <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      <input data-testid="b2b-form-email" type="email" value={form.email} onChange={set("email")} placeholder="E-Mail *" required className={`${inputCls} pl-10`} />
                    </div>
                    <div className="relative">
                      <Phone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
                      <input data-testid="b2b-form-phone" value={form.phone} onChange={set("phone")} placeholder="Telefon" className={`${inputCls} pl-10`} />
                    </div>
                  </div>
                  <div className="relative">
                    <MessageSquare size={14} className="absolute left-4 top-4 text-slate-600" />
                    <textarea
                      data-testid="b2b-form-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Ihre Anfrage — z. B. wöchentliche Fahrten Karlsruhe → Stuttgart, 2 Paletten, Start ab nächstem Monat …"
                      required
                      rows={5}
                      className={`${inputCls} pl-10 resize-none`}
                    />
                  </div>
                  <button
                    data-testid="b2b-form-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-4 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Anfrage senden — Antwort in 24 h
                  </button>
                  <p className="text-[10px] text-slate-600 text-center">
                    Mit dem Absenden stimmen Sie unserer <a href="/datenschutz" className="underline hover:text-slate-400">Datenschutzerklärung</a> zu.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Fleet strip */}
        <section className="pb-16">
          <div className="rounded-3xl border border-white/8 bg-[#121826] p-6 flex flex-wrap items-center gap-x-8 gap-y-3 justify-center text-center">
            <span className="flex items-center gap-2 text-sm text-slate-300"><Truck size={16} className="text-cyan-400" /> Ford Transit Kasten · L 4,20 m</span>
            <span className="flex items-center gap-2 text-sm text-slate-300"><Truck size={16} className="text-cyan-400" /> Opel Movano · L 3,75 m</span>
            <span className="flex items-center gap-2 text-sm text-slate-300"><Truck size={16} className="text-cyan-400" /> VW Crafter · L 3,00 m</span>
          </div>
        </section>
      </main>
    </div>
  );
}
